import * as Enums from '@/utils/enums';

/** Companies whose subscription FirstTrialDate is on or after this date get the onboarding view. */
export const ONBOARDING_CUTOFF_DATE = '2026-10-20';

/**
 * HubSpot meetings scheduling page embedded by BookDemoModal. The link stays fixed; HubSpot rotates
 * the bookings between the account managers' calendars behind it. BookDemoModal shows a "coming soon"
 * state if empty.
 */
export const HUBSPOT_DEMO_MEETING_URL = 'https://meetings.hubspot.com/hudson22/consultation-with-hudson';

/** Origins the HubSpot meetings iframe posts `meetingBookSucceeded` from. */
export const HUBSPOT_MEETINGS_ORIGINS: readonly string[] = [
    'https://meetings.hubspot.com',
    'https://meetings-eu1.hubspot.com',
];

export interface OnboardingManager {
    /** First name only, as the dashboard addresses them. */
    name: string;
    /** Empty when we have no photo for them (the avatar shows their initial). */
    photoUrl: string;
    email?: string | null;
}

/**
 * Photos of the ServCraft staff who run demos and manage accounts, keyed by lower-case first name
 * (the APIs only give names). Exported from the onboarding Figma, already circular.
 */
export const MANAGER_PHOTOS: Readonly<Record<string, string>> = {
    que: '/onboarding/que.png',
    hudson: '/onboarding/hudson.png',
    khanya: '/onboarding/khanya.png',
};

/** Runs the demo, and is who the dashboard shows until GET /AccountManager/Get names an account manager. */
export const DEMO_MANAGER: OnboardingManager = { name: 'Que', photoUrl: MANAGER_PHOTOS.que };

/** `short` is the phone copy, where the stages stack into a timeline. */
export const ONBOARDING_STAGES = [
    { title: 'Demo & Setup', description: 'Our Account Manager will help you import your customers, materials and services.', short: 'Our Account Manager will help you import your customers, materials and services.' },
    { title: 'System Training', description: 'A ServCraft expert will take you through core features that will streamline your operations.', short: 'Core features that will streamline your operations.' },
    { title: 'Test it with your team', description: 'Bring your technicians and field team onboard. This helps create a live view of your jobs.', short: 'Bring your technicians and field team onboard. This allows for a full system view.' },
    { title: 'Sign off', description: "Our account manager fine-tunes the setup. You're ready to run your business on ServCraft.", short: "Our account manager fine-tunes the setup. You're ready to run your business on ServCraft." },
];

export interface OnboardingPartner {
    partner: number;
    name: string;
    logoUrl: string;
}

// Brand marks exported from the onboarding Figma (square, already circular) — render with object-fit: contain.
// In the order "How accounting integration works" offers them.
export const ONBOARDING_PARTNERS: OnboardingPartner[] = [
    { partner: Enums.IntegrationPartner.SageOne, name: 'Sage One', logoUrl: '/onboarding/sage.png' },
    { partner: Enums.IntegrationPartner.QuickBooks, name: 'QuickBooks Online', logoUrl: '/onboarding/quickbooks.png' },
    { partner: Enums.IntegrationPartner.Xero, name: 'Xero', logoUrl: '/onboarding/xero.png' },
];

export type SyncDirection = 'both' | 'toPartner';

export interface SyncRow {
    /** The data type, as ServCraft calls it. */
    servcraft: string;
    direction: SyncDirection;
}

/** "How data syncs", the same for every partner in the design. */
export const INTEGRATION_SYNC_ROWS: SyncRow[] = [
    { servcraft: 'Customers', direction: 'both' },
    { servcraft: 'Materials & services', direction: 'both' },
    { servcraft: 'Suppliers', direction: 'both' },
    { servcraft: 'Invoices', direction: 'toPartner' },
];

/** The table's "What you can do" column. */
export const syncDirectionNote = (direction: SyncDirection, partnerName: string): string =>
    direction === 'both'
        ? `Sync to ${partnerName}, from ${partnerName}, or both.`
        : `Sync to ${partnerName}.`;

/** BookDemoCard's line for each page it appears on. */
export const DEMO_CARD_TEXT = {
    jobs: "Book a free demo and we'll help you add your jobs.",
    imports: "Book a free demo and we'll help you import your data.",
    integration: "Book a free demo and we'll help you set up integration.",
};

/** Role the onboarding invite dialog pre-selects (a value from GET /Employee/PrimaryRole). */
export const ONBOARDING_INVITE_ROLE = 'Administrator';

export const ONBOARDING_ROUTES = {
    createJob: '/job/create',
    integration: '/settings/integration/manage',
    importList: '/settings/import/list',
};

/**
 * The radius every other modal gets from the theme's Modal default (theme/mantineTheme.ts). The onboarding
 * modals are built from Modal.Root/Modal.Content, which don't read that default, so they pass it themselves.
 */
export const MODAL_RADIUS = 6;

/** Matches the job card wizard's logo cap (JobCardWizardUploadDropZone). */
export const MAX_LOGO_BYTES = 3 * 1024 ** 2;
